import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { API_URL } from "../config";
import { apiFetch } from "../utils/api";
import { subirArchivo, abrirArchivoSeguro } from "../utils/upload";
import { UNIDADES } from "../utils/unidades";
import { FORMAS_PAGO, MONEDAS } from "../utils/estados";
import { ivaFraccion, ivaPorcentaje, useParametros } from "../utils/parametros";
import { Ayuda } from "../context/AyudaContext";


const redondear = (n) => Math.round((Number(n) || 0) * 100) / 100;
const formato = (n) =>
  Number(n || 0).toLocaleString("es-NI", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const COINCIDENCIA = {
  alta: { texto: "Coincide", color: "#2f7a54", bg: "#dcf0e6" },
  media: { texto: "Revisar", color: "#9c7a1a", bg: "#f3ecd0" },
  baja: { texto: "Dudosa", color: "#9c7a1a", bg: "#f3ecd0" },
  ninguna: { texto: "Sin coincidencia", color: "#5b6472", bg: "#eef1f3" },
};

// Nombres que traen la cantidad escrita al inicio ("100 metros de cable de fibra"): la cantidad se maneja
// aparte, y dejarla en el nombre confunde cuando se aprueba otra cantidad.
const REGEX_CANTIDAD_EN_NOMBRE =
  /^s*d+([.,]d+)?s*(metros?|mts?|unidades?|und|cajas?|litros?|lts?|gal[oó]n(es)?|cubetas?|rollos?|piezas?|pares?)s+(des+)?/i;
const nombreConCantidad = (nombre) => (nombre || "").match(REGEX_CANTIDAD_EN_NOMBRE);
const nombreSinCantidad = (nombre) => {
  const limpio = (nombre || "").replace(REGEX_CANTIDAD_EN_NOMBRE, "").trim();
  return limpio ? limpio.charAt(0).toUpperCase() + limpio.slice(1) : nombre;
};

// Carga automática: sube una cotización en PDF, el sistema la lee y la muestra ya
// digitalizada para revisarla; al guardar crea la cotización normal (mismo
// POST /api/cotizaciones de siempre). Con /:cotizacionId en la dirección, la
// misma pantalla abre una cotización YA guardada para verla o cambiarla
// (mismo guardado de edición que usa el detalle de la solicitud).
function CotizacionAsistida() {
  // IVA vigente (parámetro del sistema): la pantalla se repinta cuando llega.
  useParametros();
  const { id, cotizacionId } = useParams();
  const modoEdicion = !!cotizacionId;
  const navigate = useNavigate();
  const usuario = JSON.parse(localStorage.getItem("usuario") || "null");
  const esCompras = usuario?.rol === "compras" || usuario?.rol === "administrador";
  const inputArchivo = useRef(null);

  const [proveedores, setProveedores] = useState([]);
  const [archivo, setArchivo] = useState(null);
  const archivoActual = archivo;
  const [leyendo, setLeyendo] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const [resultado, setResultado] = useState(null);
  const [solicitud, setSolicitud] = useState(null);
  const [cotizacionActual, setCotizacionActual] = useState(null);
  const [mensajeLectura, setMensajeLectura] = useState("");
  // Con algo ya aprobado, cantidades, precios y moneda se bloquean (solo se corrigen textos).
  const [hayAprobacion, setHayAprobacion] = useState(false);
  // Cotización que no se eligió (gerencia aprobó con otro proveedor o se rechazó): se muestra lo
  // que cotizó, como registro de precios, y no se puede cambiar.
  const [soloConsulta, setSoloConsulta] = useState(false);
  // Ya pagada: la forma de pago es la que registró Contabilidad y no se toca.
  const [yaPagada, setYaPagada] = useState(false);

  const [proveedorId, setProveedorId] = useState("");
  // Aviso cuando el documento dice "crédito" pero el proveedor elegido no da crédito: la casilla se desmarca sola.
  const [avisoCredito, setAvisoCredito] = useState("");
  const [moneda, setMoneda] = useState("NIO");
  const simbolo = moneda === "USD" ? "US$" : "C$";
  const nombreMoneda = moneda === "USD" ? "Dólares" : "Córdobas";
  const dinero = (n) => `${simbolo}${formato(n)}`;
  const [tipoCambio, setTipoCambio] = useState("");
  // Tipo de cambio parametrizado en Catálogos: se propone solo al usar dólares.
  const [tasaVigente, setTasaVigente] = useState(null);
  const [formaPago, setFormaPago] = useState("");
  const [tiempoEntrega, setTiempoEntrega] = useState("");
  const [lineas, setLineas] = useState([]);
  // Nombres de productos de la solicitud que compras corrigió acá: { [itemId]: texto }.
  const [nombresItem, setNombresItem] = useState({});
  // Unidades de medida corregidas acá: { [itemId]: "Metro" } (se guardan en el producto de la solicitud).
  const [unidadesItem, setUnidadesItem] = useState({});

  useEffect(() => {
    apiFetch("/api/proveedores")
      .then((data) => setProveedores(data || []))
      .catch(() => {});
    apiFetch("/api/tipo-cambio")
      .then((data) => setTasaVigente(data?.vigente || null))
      .catch(() => {});
  }, []);

  const cambiarMoneda = (valor) => {
    setMoneda(valor);
    if (valor === "USD" && !tipoCambio && tasaVigente) setTipoCambio(String(tasaVigente.tasa));
  };

  // Modo edición: arma la pantalla con lo que ya está guardado.
  useEffect(() => {
    if (!cotizacionId) return;
    apiFetch(`/api/solicitudes/${id}`)
      .then((sol) => {
        const c = (sol.cotizaciones || []).find((x) => String(x.id) === String(cotizacionId));
        if (!c) throw new Error("No se encontró la cotización.");
        const descartados = c.items_descartados || [];
        const esNoElegida = !c.seleccionada && descartados.length > 0;
        setSoloConsulta(esNoElegida);
        const idsDeLaCotizacion = new Set([...c.items.map((it) => it.id), ...descartados.map((d) => d.id)]);
        const items = (sol.items || [])
          // Solo se excluyen los productos rechazados por completo: si el gerente aprobó una
          // parte, el producto sigue siendo parte de la cotización (y trae su cantidad pedida).
          .filter(
            (it) =>
              !(it.descartado && !(Number(it.cantidad_aprobada) > 0)) &&
              // Un producto ya resuelto por completo (aprobado en parte y el resto rechazado) no se vuelve
              // a ofrecer para una cotización nueva; sí se conserva si ya es parte de la que se edita.
              (idsDeLaCotizacion.has(it.id) ||
                (it.estado_item !== "validado" &&
                  Number(it.cantidad_aprobada || 0) + Number(it.cantidad_descartada || 0) < Number(it.cantidad)))
          )
          .map((it) => ({
            id: it.id,
            numero_item: it.numero_item,
            descripcion_producto: it.descripcion_producto,
            descripcion_detallada: it.descripcion_detallada,
            // Con aprobación parcial, la cotización guardada cubre solo lo aprobado (el precio ya
            // quedó prorrateado a esa cantidad): esa es la cantidad que se muestra y se multiplica.
            cantidad: Number(it.cantidad_aprobada) > 0 ? Number(it.cantidad_aprobada) : it.cantidad,
            cantidadSolicitada: it.cantidad,
            unidad: it.unidad,
          }));
        setHayAprobacion(
          (sol.items || []).some(
            (it) => idsDeLaCotizacion.has(it.id) && (it.aprobado || Number(it.cantidad_aprobada) > 0)
          )
        );
        setYaPagada(
          (sol.pagos || []).some((p) => p.cotizacion_id === c.id && p.estado === "pagado") ||
            (sol.pagos_credito || []).some((p) => p.cotizacion_id === c.id && p.estado === "pagado")
        );
        setSolicitud(sol);
        setCotizacionActual(c);
        setResultado({
          proveedor: { nombre: c.proveedor_nombre, proveedor_id: c.proveedor_id },
          documento: { total: null },
          items_solicitud: items,
          advertencias: [],
        });
        setProveedorId(String(c.proveedor_id));
        setMoneda(c.moneda || "NIO");
        setTipoCambio(c.tipo_cambio ? String(c.tipo_cambio) : "");
        setFormaPago(c.forma_pago || "");
        setTiempoEntrega(c.tiempo_entrega || "");
        setLineas(
          [...c.items, ...(esNoElegida ? descartados : [])].map((it) => {
            const producto = items.find((i) => i.id === it.id);
            // En una no elegida se muestra la cantidad que cotizó el proveedor (no la aprobada a otro).
            const cantidad = (esNoElegida ? Number(it.cantidad) : 0) || Number(producto?.cantidad) || 1;
            // Precio unitario = lo guardado / la cantidad que cubre ese vínculo.
            const cantidadVinculo = Number(it.cantidad) || cantidad;
            return {
              codigo: null,
              descripcion: it.descripcion_producto,
              cantidad,
              unidad: producto?.unidad || null,
              precio_unitario: it.precio != null ? Math.round((Number(it.precio) / cantidadVinculo) * 10000) / 10000 : "",
              itemId: String(it.id),
            };
          })
        );
      })
      .catch((err) => setError(err.message));
  }, [id, cotizacionId]);

  // Lee el archivo elegido (si se pasa uno, lo usa; si no, el que ya está elegido).
  const leer = async (archivoElegido) => {
    const archivo = archivoElegido || archivoActual;
    if (!archivo) return;
    setLeyendo(true);
    setError("");
    setResultado(null);
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("archivo", archivo);
      formData.append("solicitud_id", id);
      const res = await fetch(`${API_URL}/api/cotizacion-asistida/leer`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "No se pudo leer la cotización.");

      setResultado(data);
      setNombresItem({});
      setUnidadesItem({});
      setProveedorId(data.proveedor.proveedor_id ? String(data.proveedor.proveedor_id) : "");
      setMoneda(data.documento.moneda);
      setTipoCambio(data.documento.moneda === "USD" && tasaVigente ? String(tasaVigente.tasa) : "");
      const proveedorLeido = proveedores.find((p) => String(p.id) === String(data.proveedor.proveedor_id));
      if (data.documento.forma_pago === "credito" && proveedorLeido && !proveedorLeido.tiene_credito) {
        setFormaPago("");
        setAvisoCredito(
          `El documento dice "crédito", pero ${proveedorLeido.nombre_empresa} no tiene crédito registrado: la forma de pago quedó sin definir (la elige Pagos).`
        );
      } else {
        // El crédito nunca se marca solo: Compras lo enciende con el botón si hace falta.
        setFormaPago(data.documento.forma_pago === "credito" ? "" : data.documento.forma_pago || "");
        setAvisoCredito("");
      }
      setTiempoEntrega(data.documento.tiempo_entrega || "");
      setLineas(
        data.lineas.map((l) => ({ ...l, itemId: l.solicitud_item_id ? String(l.solicitud_item_id) : "" }))
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setLeyendo(false);
    }
  };

  // Modo edición: lee el archivo elegido y completa SOLO los datos que estén
  // vacíos (entrega, forma de pago); no toca productos ni precios.
  const completarDatos = async () => {
    if (!archivo) return;
    setLeyendo(true);
    setError("");
    setMensajeLectura("");
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("archivo", archivo);
      formData.append("solicitud_id", id);
      formData.append("solo_datos", "1");
      const res = await fetch(`${API_URL}/api/cotizacion-asistida/leer`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "No se pudo leer el archivo.");

      const completados = [];
      if (!tiempoEntrega.trim() && data.documento.tiempo_entrega) {
        setTiempoEntrega(data.documento.tiempo_entrega);
        completados.push("Entrega");
      }
      if (!formaPago && data.documento.forma_pago && data.documento.forma_pago !== "credito") {
        setFormaPago(data.documento.forma_pago);
        completados.push("Forma de pago");
      }
      setResultado((prev) => ({
        ...prev,
        proveedor: { ...prev.proveedor, ruc: data.proveedor.ruc },
        documento: {
          ...prev.documento,
          numero: data.documento.numero,
          fecha: data.documento.fecha,
          condiciones_pago: data.documento.condiciones_pago,
          observaciones: data.documento.observaciones,
        },
      }));
      setMensajeLectura(
        completados.length > 0
          ? `Se completó: ${completados.join(" y ")}. Revísalo y guarda los cambios.`
          : "El documento no trae datos nuevos para completar. Si la forma de pago no se llenó, mira la nota que dice lo que indica el documento."
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setLeyendo(false);
    }
  };

  // Quita una línea de la cotización: el subtotal, el IVA y el total se recalculan solos con
  // lo que queda. En el detalle de una cotización guardada, el producto vuelve a la lista de
  // "productos que esta cotización no cubre" por si se quiere agregar de nuevo.
  const quitarLinea = (indice) => {
    setLineas((prev) => prev.filter((_, i) => i !== indice));
  };

  const actualizarLinea = (indice, campo, valor) => {
    setLineas((prev) => prev.map((l, i) => (i === indice ? { ...l, [campo]: valor } : l)));
  };

  const itemsSolicitud = resultado?.items_solicitud || [];
  const itemPorId = (itemId) => itemsSolicitud.find((i) => String(i.id) === String(itemId));

  const agregarProducto = (itemId) => {
    const producto = itemPorId(itemId);
    if (!producto) return;
    setLineas((prev) => [
      ...prev,
      {
        codigo: null,
        descripcion: producto.descripcion_producto,
        cantidad: producto.cantidad,
        unidad: producto.unidad,
        precio_unitario: "",
        itemId: String(producto.id),
      },
    ]);
  };

  const proveedorElegido = proveedores.find((p) => String(p.id) === String(proveedorId));
  const proveedorReconocido = resultado
    ? proveedores.find((p) => p.id === resultado.proveedor.proveedor_id)
    : null;
  const exentoIva = !!proveedorElegido?.exento_iva;

  // Precio que se registra por producto: precio unitario × la cantidad que
  // se pidió en la solicitud (así el sistema prorratea bien si el gerente
  // aprueba menos cantidad).
  const lineasIncluidas = lineas.filter((l) => l.itemId);
  // (En el detalle de una cotización guardada, compras puede cambiar la cantidad de la línea:
  // el total sale de precio unitario × esa cantidad.)
  const cantidadDeLinea = (l) => Number(l.cantidad) || 0;
  const precioDeLinea = (l) => redondear(Number(l.precio_unitario) * cantidadDeLinea(l));
  const subtotalSistema = redondear(lineasIncluidas.reduce((s, l) => s + precioDeLinea(l), 0));
  const ivaSistema = exentoIva ? 0 : redondear(subtotalSistema * ivaFraccion());
  const totalSistema = redondear(subtotalSistema + ivaSistema);

  const itemsRepetidos = useMemo(() => {
    const vistos = new Set();
    const repetidos = new Set();
    lineas.forEach((l) => {
      if (!l.itemId) return;
      if (vistos.has(l.itemId)) repetidos.add(l.itemId);
      vistos.add(l.itemId);
    });
    return repetidos;
  }, [lineas]);

  const itemsSinPrecio = itemsSolicitud.filter((i) => !lineasIncluidas.some((l) => String(l.itemId) === String(i.id)));

  // Dos momentos: los TEXTOS se corrigen hasta que la compra se cierra; cantidades, precios
  // y moneda solo antes de que se apruebe (y se pague).
  const compraCerrada = ["entregada", "rechazada"].includes(solicitud?.estado);
  const puedeEditarTodo = !modoEdicion || (solicitud?.estado === "en_cola" && !hayAprobacion);
  const puedeEditarTextos = !modoEdicion || !compraCerrada;
  const puedeEditar = puedeEditarTextos && !soloConsulta;
  const bloqueoNumeros = (modoEdicion && !puedeEditarTodo) || soloConsulta;
  const bloqueoTextos = (modoEdicion && !puedeEditarTextos) || soloConsulta;

  // Guarda los nombres corregidos (y, si todavía se puede, la cantidad) de los productos de la
  // solicitud — es el nombre que luego sale en las actas.
  const guardarProductos = async () => {
    for (const l of lineasIncluidas) {
      const producto = itemPorId(l.itemId);
      const nuevoNombre = nombresItem[l.itemId];
      const cantidadActual = Number(producto.cantidadSolicitada ?? producto.cantidad);
      const nuevaCantidad = modoEdicion && puedeEditarTodo ? Number(l.cantidad) : cantidadActual;
      const cambioNombre = nuevoNombre !== undefined && nuevoNombre.trim() !== producto.descripcion_producto.trim();
      const nuevaUnidad = unidadesItem[l.itemId] !== undefined ? unidadesItem[l.itemId] : producto.unidad;
      const cambioUnidad = (nuevaUnidad || "") !== (producto.unidad || "");
      if (!cambioNombre && !cambioUnidad && nuevaCantidad === cantidadActual) continue;
      await apiFetch(`/api/items/${producto.id}/editar`, {
        method: "POST",
        body: JSON.stringify({
          descripcion_producto: cambioNombre ? nuevoNombre.trim() : producto.descripcion_producto,
          descripcion_detallada: producto.descripcion_detallada,
          cantidad: nuevaCantidad,
          unidad: nuevaUnidad || null,
        }),
      });
    }
  };

  // Ya aprobada: solo textos (nombre del producto, entrega, forma de pago, archivo).
  const guardarSoloTextos = async () => {
    if (lineasIncluidas.some((l) => nombresItem[l.itemId] !== undefined && !nombresItem[l.itemId].trim())) {
      return setError("El nombre del producto no puede quedar vacío.");
    }
    setGuardando(true);
    try {
      await guardarProductos();
      const archivoCotizacion = archivo ? await subirArchivo(archivo) : cotizacionActual?.archivo_cotizacion;
      await apiFetch(`/api/cotizaciones/${cotizacionId}/editar-textos`, {
        method: "POST",
        body: JSON.stringify({
          tiempo_entrega: tiempoEntrega || null,
          forma_pago: formaPago || null,
          archivo_cotizacion: archivoCotizacion,
        }),
      });
      navigate(`/solicitud/${id}`);
    } catch (err) {
      setError(err.message);
      setGuardando(false);
    }
  };

  const guardar = async () => {
    setError("");
    if (modoEdicion && !puedeEditarTodo) return guardarSoloTextos();
    if (!proveedorId) return setError("Elige el proveedor de esta cotización.");
    if (lineasIncluidas.length === 0) return setError("Relaciona al menos una línea con un producto de la solicitud.");
    if (itemsRepetidos.size > 0) return setError("Hay dos líneas relacionadas con el mismo producto. Deja solo una.");
    if (lineasIncluidas.some((l) => !(precioDeLinea(l) > 0))) return setError("Todas las líneas incluidas necesitan un precio mayor a cero.");
    if (moneda === "USD" && !tipoCambio) return setError("Ingresa el tipo de cambio para convertir de dólares a córdobas.");
    if (lineasIncluidas.some((l) => nombresItem[l.itemId] !== undefined && !nombresItem[l.itemId].trim())) {
      return setError("El nombre del producto de la solicitud no puede quedar vacío.");
    }

    setGuardando(true);
    try {
      // Si compras corrigió el nombre (o la cantidad) de un producto de la solicitud, queda guardado.
      await guardarProductos();
      const archivoCotizacion = archivo ? await subirArchivo(archivo) : cotizacionActual?.archivo_cotizacion;
      const precios = {};
      const cantidades = {};
      lineasIncluidas.forEach((l) => {
        precios[l.itemId] = precioDeLinea(l);
        cantidades[l.itemId] = Number(l.cantidad);
      });
      await apiFetch(modoEdicion ? `/api/cotizaciones/${cotizacionId}/editar` : "/api/cotizaciones", {
        method: "POST",
        body: JSON.stringify({
          solicitud_id: Number(id),
          proveedor_id: Number(proveedorId),
          item_ids: lineasIncluidas.map((l) => Number(l.itemId)),
          precios,
          cantidades,
          moneda,
          tipo_cambio: moneda === "USD" ? tipoCambio : null,
          tiempo_entrega: tiempoEntrega || null,
          forma_pago: formaPago || null,
          archivo_cotizacion: archivoCotizacion,
        }),
      });
      navigate(`/solicitud/${id}`);
    } catch (err) {
      setError(err.message);
      setGuardando(false);
    }
  };

  if (!esCompras) return <p>No tienes permiso para ver esta pantalla.</p>;

  const diferenciaDocumento =
    resultado?.documento?.total != null ? redondear(totalSistema - resultado.documento.total) : null;

  return (
    <div>
      <Link to={`/solicitud/${id}`} style={styles.linkVolver}>
        ← Regresar a la solicitud #{String(id).padStart(3, "0")}
      </Link>
      <div style={styles.titulo}>
        <h2 style={{ margin: 0, color: "var(--tema-titulo, #0b3c60)" }}>
          {modoEdicion ? "Detalle de la cotización" : "Carga automática de cotización"}
        </h2>
      </div>
      <Ayuda>
      <p style={styles.subtitulo}>
        {modoEdicion
          ? "Revisa o cambia lo que necesites de esta cotización y guarda los cambios."
          : "Sube la cotización del proveedor en PDF digital (el que permite seleccionar texto) y el sistema lee los datos y los precios por ti. Revisa lo que leyó y guárdala como cotización normal."}
      </p>
      </Ayuda>

      <div style={styles.tarjeta}>
        <input
          ref={inputArchivo}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          style={{ display: "none" }}
          onChange={(e) => {
            const elegido = e.target.files[0] || null;
            setArchivo(elegido);
            if (!modoEdicion) setResultado(null);
            setError("");
            setMensajeLectura("");
            // Al subir la cotización se lee sola, sin tener que tocar "Leer cotización".
            if (!modoEdicion && elegido) leer(elegido);
            e.target.value = "";
          }}
        />
        <div style={{ display: "flex", alignItems: "center", gap: "0.8rem", flexWrap: "wrap" }}>
          <button type="button" onClick={() => inputArchivo.current?.click()} style={styles.botonSecundario} disabled={leyendo || guardando || bloqueoTextos}>
            📎 {modoEdicion ? "Reemplazar archivo" : archivo ? "Cambiar archivo" : "Elegir cotización"}
          </button>
          <span style={{ fontSize: "0.85rem", color: "#5b6472" }}>
            {archivo
              ? archivo.name
              : modoEdicion
              ? "Se conserva el archivo actual si no eliges otro"
              : "PDF digital del proveedor (con texto seleccionable) — máximo 10 MB"}
          </span>
          {modoEdicion && cotizacionActual?.archivo_cotizacion && (
            <a href="#" onClick={(e) => abrirArchivoSeguro(e, cotizacionActual.archivo_cotizacion)} style={styles.linkArchivo}>
              📄 Ver archivo actual
            </a>
          )}
          {!modoEdicion && (
            <button type="button" onClick={() => leer()} disabled={!archivo || leyendo || guardando} style={styles.botonPrimario}>
              {leyendo ? "Leyendo la cotización…" : "✨ Leer cotización"}
            </button>
          )}
          {modoEdicion && archivo && (
            <button type="button" onClick={completarDatos} disabled={leyendo || guardando} style={styles.botonPrimario}>
              {leyendo ? "Leyendo el archivo…" : "✨ Leer este archivo y completar datos"}
            </button>
          )}
        </div>
        {mensajeLectura && <p style={{ fontSize: "0.84rem", color: "#2f7a54", margin: "0.7rem 0 0" }}>{mensajeLectura}</p>}
        {leyendo && (
          <Ayuda>
          <p style={{ fontSize: "0.82rem", color: "#5b6472", margin: "0.7rem 0 0" }}>
            Esto puede tardar entre 10 y 40 segundos según el tamaño del documento.
          </p>
          </Ayuda>
        )}
      </div>

      {error && <p style={styles.error}>{error}</p>}
      {modoEdicion && solicitud && soloConsulta && (
        <div style={styles.avisoAdvertencia}>
          Esta cotización <strong>no fue elegida</strong>. Se muestra lo que cotizó {cotizacionActual?.proveedor_nombre || "el proveedor"} como
          registro de precios; no se puede cambiar.
        </div>
      )}
      {modoEdicion && solicitud && bloqueoTextos && !soloConsulta && (
        <div style={styles.avisoAdvertencia}>La compra ya está cerrada, así que la cotización se puede ver pero no cambiar.</div>
      )}
      {modoEdicion && solicitud && !bloqueoTextos && bloqueoNumeros && (
        <div style={styles.avisoAdvertencia}>
          Esta cotización ya fue aprobada: puedes corregir los textos (nombre del producto, forma de pago, entrega y
          archivo), pero las cantidades, los precios y la moneda ya no se pueden cambiar.
        </div>
      )}

      {resultado && (
        <>
          {resultado.advertencias.length > 0 && (
            <div style={styles.avisoAdvertencia}>
              <strong>Revisa estos puntos:</strong>
              <ul style={{ margin: "0.3rem 0 0", paddingLeft: "1.1rem" }}>
                {resultado.advertencias.map((a, i) => (
                  <li key={i}>{a}</li>
                ))}
              </ul>
            </div>
          )}

          <div style={{ ...styles.tarjeta, ...styles.tarjetaDatos }}>
            <div style={styles.encabezadoDatos}>
              <div className="form-seccion-titulo" style={{ margin: 0 }}>🧾 Datos de la cotización</div>
              {/* Lo que se leyó del documento, a la vista (no es un mensaje de guía). */}
              <div style={styles.fichaDocumento}>
                <span
                  style={{
                    ...styles.chipDato,
                    ...(modoEdicion
                      ? styles.chipDatoGuardada
                      : proveedorReconocido
                      ? styles.chipDatoOk
                      : styles.chipDatoAviso),
                  }}
                >
                  {modoEdicion ? "💾 Guardada" : proveedorReconocido ? "✓ Proveedor reconocido" : "⚠ Proveedor no reconocido"}
                  {": "}
                  <strong>
                    {proveedorReconocido ? proveedorReconocido.nombre_empresa : resultado.proveedor.nombre || "—"}
                  </strong>
                </span>
                {resultado.proveedor.ruc && <span style={styles.chipDato}>RUC {resultado.proveedor.ruc}</span>}
                {resultado.documento.numero && <span style={styles.chipDato}>N.º {resultado.documento.numero}</span>}
                {resultado.documento.fecha && (
                  <span style={styles.chipDato}>📅 {resultado.documento.fecha.split("-").reverse().join("/")}</span>
                )}
              </div>
            </div>
            <div style={styles.gridDatos}>
              <label style={styles.campo}>
                Proveedor registrado
                <select
                  value={proveedorId}
                  onChange={(e) => {
                    const nuevo = proveedores.find((p) => String(p.id) === String(e.target.value));
                    setProveedorId(e.target.value);
                    // Un proveedor sin crédito no puede llevar una compra a crédito: se desmarca y se avisa.
                    if (formaPago === "credito" && nuevo && !nuevo.tiene_credito && !(modoEdicion && yaPagada)) {
                      setFormaPago("");
                      setAvisoCredito(
                        `${nuevo.nombre_empresa} no tiene crédito registrado: la forma de pago quedó sin definir (la elige Pagos). Si sí da crédito, márcalo en Catálogos → Proveedores.`
                      );
                      setError("");
                    } else if (nuevo?.tiene_credito || formaPago !== "credito") {
                      setAvisoCredito("");
                    }
                  }}
                  disabled={bloqueoNumeros}
                  style={styles.input}
                >
                  <option value="">Elegir proveedor…</option>
                  {proveedores.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nombre_empresa}
                    </option>
                  ))}
                </select>
                {!modoEdicion && !resultado.proveedor.proveedor_id && (
                  <span style={styles.notaCampo}>No se encontró en el catálogo — elígelo o regístralo antes en Catálogos.</span>
                )}
              </label>
              <label style={styles.campo}>
                Moneda
                <select value={moneda} onChange={(e) => cambiarMoneda(e.target.value)} disabled={bloqueoNumeros} style={styles.input}>
                  {MONEDAS.map((m) => (
                    <option key={m.valor} value={m.valor}>
                      {m.texto}
                    </option>
                  ))}
                </select>
              </label>
              {moneda === "USD" && (
                <label style={styles.campo}>
                  Tipo de cambio (C$ por US$)
                  <input type="number" step="0.0001" value={tipoCambio} onChange={(e) => setTipoCambio(e.target.value)} disabled={bloqueoNumeros} style={styles.input} />
                  {tasaVigente ? (
                    <span style={styles.notaCampo}>
                      Vigente en Catálogos: C${tasaVigente.tasa.toFixed(4)} (desde{" "}
                      {new Date(tasaVigente.desde).toLocaleDateString("es-NI")}).
                    </span>
                  ) : (
                    <span style={styles.notaCampo}>
                      Aún no hay un tipo de cambio guardado en Catálogos → Tipo de cambio.
                    </span>
                  )}
                </label>
              )}
              {/* div y no label: dentro hay dos controles (casilla y selector) */}
              <div style={styles.campo}>
                {/* "Crédito" es una CONDICIÓN de la compra (el proveedor entrega y se le
                    paga después), no un medio de pago: Compras la marca aquí, antes de
                    que pase al aprobador, y solo si el proveedor da crédito. Cómo se
                    paga (transferencia, cheque…) lo decide Pagos al pagar la factura.
                    El interruptor de crédito va en la misma línea del título para que
                    la caja quede alineada con los demás campos. */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.5rem" }}>
                  <span>Forma de pago</span>
                  {(proveedorElegido?.tiene_credito || formaPago === "credito") && (
                    <button
                      type="button"
                      onClick={() => setFormaPago(formaPago === "credito" ? "" : "credito")}
                      disabled={bloqueoTextos || (modoEdicion && yaPagada)}
                      aria-pressed={formaPago === "credito"}
                      title={`${proveedorElegido?.nombre_empresa || "El proveedor"} entrega y se le paga después (30 días desde que se recibe).`}
                      style={{
                        ...styles.chipCredito,
                        ...(formaPago === "credito" ? styles.chipCreditoActivo : {}),
                      }}
                    >
                      {formaPago === "credito" ? "✓ " : ""}🤝 Crédito
                    </button>
                  )}
                </div>
                {formaPago === "credito" ? (
                  <div style={{ ...styles.input, background: "#fff", fontWeight: 600, textTransform: "none", letterSpacing: "normal", color: "var(--tema-titulo, #0b3c60)" }}>
                    🤝 Crédito (30 días)
                  </div>
                ) : (
                  <select aria-label="Forma de pago" value={formaPago} onChange={(e) => setFormaPago(e.target.value)} disabled={bloqueoTextos || (modoEdicion && yaPagada)} style={styles.input}>
                    <option value="">Elegir…</option>
                    {FORMAS_PAGO.filter((f) => f.valor !== "credito").map((f) => (
                      <option key={f.valor} value={f.valor}>
                        {f.texto}
                      </option>
                    ))}
                  </select>
                )}
                {avisoCredito && <span style={{ ...styles.notaCampo, color: "#9c7a1a" }}>{avisoCredito}</span>}
                {formaPago === "credito" && (
                  <Ayuda>
                  <span style={styles.notaCampo}>
                    El medio de pago (transferencia, cheque…) lo elige Pagos al pagar la factura.
                  </span>
                  </Ayuda>
                )}
                {modoEdicion && yaPagada && (
                  <Ayuda>
                    <span style={styles.notaCampo}>Ya está pagada: es la forma con la que pagó Contabilidad.</span>
                  </Ayuda>
                )}
                {resultado.documento.condiciones_pago && (
                  <span style={styles.notaCampo}>El documento dice: “{resultado.documento.condiciones_pago}”</span>
                )}
              </div>
              <label style={styles.campo}>
                Entrega
                <input type="text" value={tiempoEntrega} onChange={(e) => setTiempoEntrega(e.target.value)} disabled={bloqueoTextos} style={styles.input} />
              </label>
            </div>
            {resultado.documento.observaciones && (
              <p style={{ margin: "0.8rem 0 0", fontSize: "0.82rem", color: "#5b6472" }}>
                📝 {resultado.documento.observaciones}
              </p>
            )}
          </div>

          <div style={styles.tarjeta}>
            <h3 style={styles.tituloSeccion}>
              {modoEdicion ? "Productos de la cotización" : "Productos leídos y relacionados con la solicitud"}
            </h3>
            <div style={{ overflowX: "auto" }}>
              <table style={styles.tabla}>
                <thead>
                  <tr>
                    <th style={styles.th}>#</th>
                    <th style={styles.th}>Producto en la cotización</th>
                    <th style={{ ...styles.th, textAlign: "right" }}>Cant.</th>
                    <th style={styles.th}>Unidad</th>
                    <th style={{ ...styles.th, textAlign: "right" }}>P.Unit ({simbolo})</th>
                    <th style={{ ...styles.th, textAlign: "right" }}>Total</th>
                    <th style={styles.th}>Producto de la solicitud</th>
                    <th style={styles.th}>Coincidencia</th>
                    {!bloqueoNumeros && <th style={styles.th}></th>}
                  </tr>
                </thead>
                <tbody>
                  {lineas.map((l, i) => {
                    const item = itemPorId(l.itemId);
                    const nombreSolicitud = item ? nombresItem[item.id] ?? item.descripcion_producto : "";
                    // Si la línea ya tiene un producto de la solicitud elegido (por el sistema o por compras), coincide.
                    const nivel = item ? "alta" : "ninguna";
                    const info = COINCIDENCIA[nivel];
                    const repetido = l.itemId && itemsRepetidos.has(l.itemId);
                    const cantidadDistinta = !modoEdicion && item && Number(l.cantidad) !== Number(item.cantidad);
                    return (
                      <tr key={i} style={{ ...styles.fila, opacity: l.itemId ? 1 : 0.55, background: l.revisar ? "#fdf6e3" : undefined }}>
                        <td style={styles.td}>{i + 1}</td>
                        <td style={{ ...styles.td, textAlign: "left" }}>
                          {l.codigo && <span style={styles.codigo}>{l.codigo}</span>}
                          <input
                            type="text"
                            value={modoEdicion && item ? nombresItem[item.id] ?? item.descripcion_producto : l.descripcion}
                            onChange={(e) =>
                              modoEdicion && item
                                ? setNombresItem((prev) => ({ ...prev, [item.id]: e.target.value }))
                                : actualizarLinea(i, "descripcion", e.target.value)
                            }
                            disabled={bloqueoTextos}
                            style={{ ...styles.input, width: "100%", boxSizing: "border-box", marginTop: l.codigo ? "0.25rem" : 0 }}
                          />
                        </td>
                        <td style={{ ...styles.td, textAlign: "right" }}>
                          <input
                            type="number"
                            min="0"
                            value={l.cantidad}
                            onChange={(e) => actualizarLinea(i, "cantidad", e.target.value)}
                            disabled={bloqueoNumeros}
                            style={{ ...styles.input, width: 70, textAlign: "right" }}
                          />
                        </td>
                        <td style={styles.td}>
                          {item ? (
                            <select
                              value={unidadesItem[item.id] !== undefined ? unidadesItem[item.id] : item.unidad || ""}
                              onChange={(e) => setUnidadesItem((prev) => ({ ...prev, [item.id]: e.target.value }))}
                              disabled={bloqueoTextos}
                              title="Unidad de medida del producto (se guarda en la solicitud)"
                              style={{ ...styles.input, width: 110 }}
                            >
                              <option value="">—</option>
                              {[...new Set([...UNIDADES, item.unidad].filter(Boolean))].map((u) => (
                                <option key={u} value={u}>
                                  {u}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span style={{ color: "#8993a1" }}>{l.unidad || "—"}</span>
                          )}
                        </td>
                        <td style={{ ...styles.td, textAlign: "right" }}>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={l.precio_unitario}
                            onChange={(e) => actualizarLinea(i, "precio_unitario", e.target.value)}
                            disabled={bloqueoNumeros}
                            style={{ ...styles.input, width: 90, textAlign: "right" }}
                          />
                        </td>
                        <td style={{ ...styles.td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                          {dinero(Number(l.cantidad) * Number(l.precio_unitario))}
                        </td>
                        <td style={{ ...styles.td, textAlign: "left" }}>
                          <select
                            value={l.itemId}
                            onChange={(e) => actualizarLinea(i, "itemId", e.target.value)}
                            disabled={bloqueoNumeros}
                            style={{ ...styles.input, maxWidth: 260, borderColor: repetido ? "#a3402f" : undefined }}
                          >
                            <option value="">— No incluir —</option>
                            {itemsSolicitud.map((it) => {
                              // Un producto de la solicitud solo puede ir en una línea de la cotización.
                              const usadoEnOtra = lineas.some((x, k) => k !== i && String(x.itemId) === String(it.id));
                              return (
                                <option key={it.id} value={it.id} disabled={usadoEnOtra}>
                                  #{it.numero_item} {it.descripcion_producto} ({it.cantidad}){usadoEnOtra ? " — ya asignado" : ""}
                                </option>
                              );
                            })}
                          </select>
                          {/* El nombre del producto de la solicitud se corrige en el Detalle de la cotización guardada. */}
                          {item && modoEdicion && (
                            <>
                              <input
                                type="text"
                                value={nombreSolicitud}
                                onChange={(e) => setNombresItem((prev) => ({ ...prev, [item.id]: e.target.value }))}
                                title="Nombre del producto en la solicitud (se guarda al guardar la cotización)"
                                disabled={bloqueoTextos}
                                style={{ ...styles.input, width: "100%", maxWidth: 260, boxSizing: "border-box", marginTop: "0.3rem" }}
                              />
                              {nombresItem[item.id] !== undefined &&
                                nombresItem[item.id].trim() !== item.descripcion_producto.trim() && (
                                  <Ayuda>
                                  <div style={styles.notaCampo}>Al guardar, la solicitud quedará con este nombre.</div>
                                  </Ayuda>
                                )}
                              {!bloqueoTextos && nombreConCantidad(nombreSolicitud) && (
                                <div style={styles.notaCampo}>
                                  El nombre incluye la cantidad ("{nombreConCantidad(nombreSolicitud)[0].trim()}"), que ya se
                                  maneja aparte.{" "}
                                  <button
                                    type="button"
                                    onClick={() => setNombresItem((prev) => ({ ...prev, [item.id]: nombreSinCantidad(nombreSolicitud) }))}
                                    style={styles.botonAgregar}
                                  >
                                    Quitarla del nombre
                                  </button>
                                </div>
                              )}
                            </>
                          )}
                          {cantidadDistinta && (
                            <div style={styles.notaCampo}>
                              Cotizó {l.cantidad} de las {item.cantidad} que se pidieron.
                            </div>
                          )}
                          {repetido && <div style={{ ...styles.notaCampo, color: "#a3402f" }}>Producto repetido en otra línea.</div>}
                        </td>
                        <td style={styles.td}>
                          <span style={{ ...styles.badge, color: info.color, background: info.bg }}>{info.texto}</span>
                        </td>
                        {!bloqueoNumeros && (
                          <td style={styles.td}>
                            <button
                              type="button"
                              onClick={() => quitarLinea(i)}
                              title="Quitar este producto de la cotización"
                              style={styles.botonQuitar}
                            >
                              🗑️
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {itemsSinPrecio.length > 0 && !bloqueoNumeros && (
              <div style={{ margin: "0.8rem 0 0", fontSize: "0.82rem", color: "#9c7a1a" }}>
                Productos de la solicitud que esta cotización no cubre:
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginTop: "0.4rem" }}>
                  {itemsSinPrecio.map((i) => (
                    <button key={i.id} type="button" onClick={() => agregarProducto(i.id)} style={styles.botonAgregar}>
                      + #{i.numero_item} {i.descripcion_producto}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div style={styles.cajaTotal}>
            <div style={styles.tituloCajaTotal}>
              Total de la cotización en {nombreMoneda} ({simbolo})
            </div>
            <div style={styles.filaCajaTotal}>
              <span>Subtotal ({lineasIncluidas.length} producto{lineasIncluidas.length === 1 ? "" : "s"})</span>
              <strong>{dinero(subtotalSistema)}</strong>
            </div>
            <div style={styles.filaCajaTotal}>
              <span>IVA {ivaPorcentaje()}%{exentoIva ? " (exonerado)" : ""}</span>
              <strong>{dinero(ivaSistema)}</strong>
            </div>
            <div style={{ ...styles.filaCajaTotal, borderBottom: "none", paddingTop: "0.4rem" }}>
              <span>Total</span>
              <strong style={{ color: "#2f7a54", fontSize: "1.1rem" }}>{dinero(totalSistema)}</strong>
            </div>
            {diferenciaDocumento != null && Math.abs(diferenciaDocumento) > 0.05 && totalSistema === 0 && (
              <p style={{ margin: "0.6rem 0 0", fontSize: "0.76rem", color: "#9c7a1a", maxWidth: 300 }}>
                Todavía no incluiste ningún producto: elige a qué producto de la solicitud corresponde cada línea
                del documento (el documento indica un total de {dinero(resultado.documento.total)}).
              </p>
            )}
            {diferenciaDocumento != null && Math.abs(diferenciaDocumento) > 0.05 && totalSistema !== 0 && (
              <p style={{ margin: "0.6rem 0 0", fontSize: "0.76rem", color: "#9c7a1a", maxWidth: 300 }}>
                El documento indica un total de {dinero(resultado.documento.total)}, que difiere en{" "}
                {dinero(Math.abs(diferenciaDocumento))}. Es normal si no incluiste todas sus líneas o si el
                documento trae el IVA distinto.
              </p>
            )}
          </div>

          <div style={{ display: "flex", gap: "0.6rem", marginTop: "1rem" }}>
            {puedeEditar && (
              <button type="button" onClick={guardar} disabled={guardando} style={styles.botonGuardar}>
                {guardando ? "Guardando…" : modoEdicion ? "💾 Guardar cambios" : "💾 Guardar como cotización"}
              </button>
            )}
            <button type="button" onClick={() => navigate(`/solicitud/${id}`)} disabled={guardando} style={styles.botonSecundario}>
              {puedeEditar ? "Cancelar" : "Regresar"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

const styles = {
  linkVolver: { color: "var(--tema-titulo, #0b3c60)", fontWeight: 600, fontSize: "0.88rem", textDecoration: "none" },
  titulo: { display: "flex", alignItems: "center", justifyContent: "center", gap: "0.7rem", margin: "0.6rem 0 0.2rem", flexWrap: "wrap" },
  subtitulo: { margin: "0 auto 1rem", color: "#5b6472", fontSize: "0.88rem", maxWidth: 720, textAlign: "center" },
  tarjeta: { background: "#fff", border: "1px solid #e2e6ea", borderRadius: 12, padding: "1rem 1.1rem", marginBottom: "0.9rem", boxShadow: "0 1px 3px rgba(27,42,74,0.06)" },
  tituloSeccion: { margin: "0 0 0.6rem", fontSize: "1rem", color: "var(--tema-titulo, #0b3c60)", textAlign: "center" },
  tarjetaDatos: { borderTop: "3px solid var(--tema-primario, #1f4e8c)", padding: "0.9rem 1.1rem 1.1rem" },
  encabezadoDatos: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "0.5rem 1rem",
    marginBottom: "0.8rem",
  },
  fichaDocumento: { display: "flex", flexWrap: "wrap", gap: "0.35rem", justifyContent: "flex-end" },
  chipDato: {
    fontSize: "0.74rem",
    color: "#3d4a5c",
    background: "#f4f6f9",
    border: "1px solid #e2e6ea",
    borderRadius: 999,
    padding: "0.18rem 0.6rem",
    whiteSpace: "nowrap",
  },
  chipDatoOk: { background: "#e6f4ec", borderColor: "#c4e3d1", color: "#2f7a54" },
  chipDatoAviso: { background: "#fdf3d7", borderColor: "#efd98f", color: "#7a5c00" },
  chipDatoGuardada: { background: "#e8f0fa", borderColor: "#c9daf0", color: "#1d4f8c" },
  gridDatos: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
    gap: "0.9rem",
    background: "var(--tema-suave, #f5f8fc)",
    border: "1px solid var(--tema-linea, #e2e8f0)",
    borderRadius: 10,
    padding: "0.85rem 0.9rem",
  },
  campo: {
    display: "flex",
    flexDirection: "column",
    gap: "0.3rem",
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.03em",
    textTransform: "uppercase",
    color: "#5b6472",
  },
  // Las notas no heredan las mayúsculas ni la negrita del título del campo.
  notaCampo: { fontSize: "0.74rem", color: "#8993a1", fontWeight: 400, textTransform: "none", letterSpacing: "normal" },
  // Pequeño para que la línea del título mida lo mismo que en los demás campos.
  chipCredito: {
    fontSize: "0.7rem",
    fontWeight: 600,
    lineHeight: 1,
    padding: "2px 8px",
    borderRadius: 999,
    border: "1px solid #d3d9df",
    background: "#fff",
    color: "#5b6472",
    cursor: "pointer",
    fontFamily: "inherit",
    whiteSpace: "nowrap",
  },
  chipCreditoActivo: {
    background: "#fdf3d7",
    borderColor: "#e3c766",
    color: "#7a5c00",
  },
  input: { border: "1px solid #d3d9df", borderRadius: 8, padding: "0.4rem 0.55rem", fontSize: "0.85rem", fontFamily: "inherit" },
  tabla: { width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" },
  th: { textAlign: "center", padding: "0.5rem 0.4rem", fontSize: "0.75rem", color: "var(--tema-titulo, #0b3c60)", background: "#eef1f3", borderBottom: "1px solid #d3d9df", whiteSpace: "nowrap" },
  td: { padding: "0.45rem 0.4rem", textAlign: "center", verticalAlign: "top" },
  fila: { borderBottom: "1px solid #eef1f3" },
  codigo: { background: "#eef1f3", borderRadius: 6, padding: "0.05rem 0.4rem", fontSize: "0.72rem", color: "#5b6472" },
  botonQuitar: { border: "1px solid #d3d9df", background: "#fff", borderRadius: 6, padding: "0.25rem 0.5rem", cursor: "pointer", fontSize: "0.85rem" },
  badge: { borderRadius: 999, padding: "0.15rem 0.6rem", fontSize: "0.74rem", fontWeight: 700, whiteSpace: "nowrap" },
  // Cajita a la derecha que se ajusta al contenido (no ocupa todo el ancho).
  cajaTotal: {
    width: "fit-content",
    minWidth: 260,
    maxWidth: "100%",
    marginLeft: "auto",
    background: "#fff",
    border: "1px solid #e2e6ea",
    borderRadius: 12,
    padding: "0.7rem 1rem",
    marginBottom: "0.9rem",
    boxShadow: "0 1px 3px rgba(27,42,74,0.06)",
  },
  tituloCajaTotal: { fontSize: "0.88rem", fontWeight: 700, color: "var(--tema-titulo, #0b3c60)", paddingBottom: "0.45rem", marginBottom: "0.2rem", borderBottom: "1px solid #e2e6ea" },
  filaCajaTotal: { display: "flex", justifyContent: "space-between", gap: "1.5rem", padding: "0.25rem 0", fontSize: "0.86rem", color: "var(--tema-titulo, #0b3c60)", borderBottom: "1px dashed #eef1f3" },
  avisoAdvertencia: { background: "#f2e3d1", color: "#7a4a17", borderRadius: 10, padding: "0.7rem 0.9rem", fontSize: "0.83rem", marginBottom: "0.9rem" },
  error: { color: "#a3402f", fontSize: "0.88rem", margin: "0.4rem 0 0.8rem" },
  linkArchivo: { color: "var(--tema-primario, #2f6690)", fontWeight: 600, fontSize: "0.85rem", textDecoration: "none" },
  botonAgregar: { background: "#fff", color: "var(--tema-titulo, #0b3c60)", border: "1px solid #d3d9df", borderRadius: 999, padding: "0.2rem 0.65rem", fontSize: "0.76rem", fontWeight: 600, cursor: "pointer" },
  botonPrimario: { background: "var(--tema-titulo, #0b3c60)", color: "#fff", border: "none", borderRadius: 8, padding: "0.55rem 1.1rem", fontSize: "0.88rem", fontWeight: 700, cursor: "pointer" },
  botonGuardar: { background: "#2f7a54", color: "#fff", border: "none", borderRadius: 8, padding: "0.6rem 1.2rem", fontSize: "0.88rem", fontWeight: 700, cursor: "pointer" },
  botonSecundario: { background: "#fff", color: "var(--tema-titulo, #0b3c60)", border: "1px solid #d3d9df", borderRadius: 8, padding: "0.55rem 1.1rem", fontSize: "0.88rem", fontWeight: 600, cursor: "pointer" },
};

export default CotizacionAsistida;
